import { ErrorHandler, Injectable, NgZone } from "@angular/core";
import { ModalService } from "../../services/modal.service";
import { take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { HttpResponse } from "@angular/common/http";
import { ErrorMessageKey } from "./error-message-key.model";
import { MsgCode } from "../../models/enum/msg-code.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AuthService } from "../../services/auth.service";

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorAndExceptionHandler implements ErrorHandler {

  CHUNK_LOAD_ERROR_MSG = 'Failed to fetch dynamically imported module';
  private isErrorModalOpen = false;

  constructor(
    private modalService: ModalService,
    private translate: TranslateService,
    private authService: AuthService,
    private zone: NgZone,
    private snackBar: MatSnackBar
  ) {
  }


  /**
   * Handles errors and determines if the error is a PWA chunk load error. If so, it displays an error message
   * using a snack bar with a specific translated message.
   */
  handleError(error: any): void {
    const isChunkLoadError = error?.message && error.message.includes(this.CHUNK_LOAD_ERROR_MSG);
    if (isChunkLoadError) {

      const translatedMessage = this.translate.instant(ErrorMessageKey.CHUNK_LOAD_ERROR);
      const translatedAction = this.translate.instant("shared.reload");

      this.zone.run(() => {
        const snackBarRef = this.snackBar.open(translatedMessage, translatedAction, {
          panelClass: ['snackbar-error'],
          duration: 10000,
        });

        snackBarRef.onAction().subscribe(() => {
          window.location.reload();
        });
      });
    }
  }

  handleAndDisplayError(error: any, trackingCode: string, showErrorReport: boolean = false): string {
    const {message, showReport} = this.getGroupedErrorMessage(error, showErrorReport);
    this.showErrorModalOnce(message, trackingCode, showReport);
    return message;
  }

  handleBusinessResponse(response: HttpResponse<any>, trackingCode: string, showErrorReport: boolean = true, clearSession: boolean = false): void {
    const responseCode = response.body?.acknowledgment?.responseCode;
    const messageCode = response.body?.acknowledgment?.acknowledgmentDetailList?.[0]?.messageCode;
    const defaultMessage = this.translate.instant(ErrorMessageKey.BACKUP);

    // TODO: Specific business exceptions/errors can be handled in future with messageCodes (what message to display or actions to take).
    //  DUMMY_EXCEPTION is added as showcase and not passed currently from anywhere. If real case is handled then dummy must be removed and replace with real case.
    switch (messageCode) {
      case MsgCode.DUMMY_EXCEPTION:
        this.showErrorModalOnce(
          `${messageCode} status: ${response.status} code: ${responseCode}`,
          trackingCode,
          showErrorReport
        );
        break;

      default:
        // Unhandled/Unknown businessException fall into a default case (ResponseCodes: "ERR", "ERROR", "REJ").
        this.showErrorModalOnce(
          `${messageCode || defaultMessage} status: ${response.status} code: ${responseCode}`,
          trackingCode,
          showErrorReport
        );
        if (clearSession) {
          this.authService.clearSession();
        }
        break;
    }
  }

  /**
   * Determines an appropriate error message and whether to show an error report
   * based on the HTTP error status code.
   *
   */
  private getGroupedErrorMessage(
    error: any,
    defaultShowReport: boolean
  ): { message: string; showReport: boolean } {

    // Early return 'unknown error' if the error is missing or malformed.
    if (!error || typeof error.status !== 'number') {
      return {
        message: ErrorMessageKey.UNKNOWN,
        showReport: true
      };
    }

    const status = error.status;

    switch (true) {
      case status >= 500 && status < 600:
        return {
          message: ErrorMessageKey.SERVER,
          showReport: defaultShowReport
        };

      case status >= 400 && status < 500:
        return {
          message: ErrorMessageKey.CLIENT,
          showReport: defaultShowReport
        };

      case status === 0:
        return {
          message: ErrorMessageKey.NETWORK,
          showReport: true
        };

      default:
        return {
          message: ErrorMessageKey.UNKNOWN,
          showReport: true
        };
    }
  }


  /**
   * Displays an error modal dialog only once, ensuring that multiple modals cannot be opened at the same time.
   */
  private showErrorModalOnce(message: string, trackingCode: string, showErrorReport: boolean): void {
    if (this.isErrorModalOpen) return;

    this.isErrorModalOpen = true;
    const codeToUse = showErrorReport ? trackingCode : '';

    this.modalService.showErrorModal(message, codeToUse, showErrorReport);
    this.modalService.modalClosed.pipe(take(1)).subscribe(() => {
      this.isErrorModalOpen = false;
    });
  }
}
